import React from 'react';

// Lightweight, dependency-free markdown renderer for chat replies.
// Supports: headings, bold, italic, inline code, code blocks, ordered &
// unordered lists, blockquotes, links and paragraphs. Builds React elements
// (no dangerouslySetInnerHTML) so it is XSS-safe.

function renderInline(text, keyBase = '') {
  const nodes = [];
  const regex = /(\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\))/g;
  let last = 0;
  let m;
  let k = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[2] !== undefined) nodes.push(<strong key={`${keyBase}b${k++}`}>{m[2]}</strong>);
    else if (m[3] !== undefined) nodes.push(<em key={`${keyBase}i${k++}`}>{m[3]}</em>);
    else if (m[4] !== undefined) nodes.push(<code key={`${keyBase}c${k++}`} className="px-1 py-0.5 rounded bg-black/10 dark:bg-white/10 text-[0.85em] font-mono">{m[4]}</code>);
    else if (m[5] !== undefined) nodes.push(<a key={`${keyBase}a${k++}`} href={m[6]} target="_blank" rel="noreferrer" className="underline underline-offset-2">{m[5]}</a>);
    last = regex.lastIndex;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export const MarkdownLite = ({ content = '', className = '' }) => {
  const lines = String(content).replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Code block
    if (/^```/.test(line.trim())) {
      const code = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i].trim())) { code.push(lines[i]); i++; }
      i++; // skip closing fence
      blocks.push(
        <pre key={key++} className="my-2 p-3 rounded-lg bg-black/80 text-slate-100 text-xs font-mono overflow-x-auto">
          <code>{code.join('\n')}</code>
        </pre>
      );
      continue;
    }

    // Heading
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      const level = h[1].length;
      const sizes = { 1: 'text-lg font-bold', 2: 'text-base font-bold', 3: 'text-sm font-semibold' };
      blocks.push(<p key={key++} className={`mt-2 mb-1 ${sizes[level] || 'text-sm font-semibold'}`}>{renderInline(h[2], `h${key}`)}</p>);
      i++; continue;
    }

    // Unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, '')); i++;
      }
      blocks.push(
        <ul key={key++} className="my-1.5 ml-5 list-disc space-y-0.5">
          {items.map((it, idx) => <li key={idx}>{renderInline(it, `ul${key}-${idx}`)}</li>)}
        </ul>
      );
      continue;
    }

    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, '')); i++;
      }
      blocks.push(
        <ol key={key++} className="my-1.5 ml-5 list-decimal space-y-0.5">
          {items.map((it, idx) => <li key={idx}>{renderInline(it, `ol${key}-${idx}`)}</li>)}
        </ol>
      );
      continue;
    }

    // Blockquote
    if (/^\s*>\s?/.test(line)) {
      const quote = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) { quote.push(lines[i].replace(/^\s*>\s?/, '')); i++; }
      blocks.push(<blockquote key={key++} className="my-2 pl-3 border-l-2 border-primary/40 text-muted-foreground italic">{renderInline(quote.join(' '), `q${key}`)}</blockquote>);
      continue;
    }

    // Blank line
    if (line.trim() === '') { i++; continue; }

    // Paragraph (gather consecutive non-blank, non-special lines)
    const para = [];
    while (i < lines.length && lines[i].trim() !== ''
      && !/^```/.test(lines[i].trim()) && !/^(#{1,6})\s+/.test(lines[i])
      && !/^\s*[-*]\s+/.test(lines[i]) && !/^\s*\d+\.\s+/.test(lines[i])
      && !/^\s*>\s?/.test(lines[i])) {
      para.push(lines[i]); i++;
    }
    blocks.push(<p key={key++} className="my-1 leading-relaxed">{renderInline(para.join('\n'), `p${key}`)}</p>);
  }

  return <div className={className}>{blocks}</div>;
};
